import {
  useProjectId,
  usePackageId,
  useLazyGetFindingQuery,
  useMessageContext,
  useServiceNameOrNull,
} from '@consigli/hooks';
import { Finding } from '@consigli/types';
import { DocumentType } from '@consigli/types';
import clsx from 'clsx';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useViewerContext } from '@/features/document-viewer/use-viewer-context';
import { getFindingMessage } from '@/util/finding-message';
import { findServiceConfigByRoute } from '@/util/service-config';

import { isDocumentDoubleViewerCapable } from './container';
import { UpdateFindingButtons } from './update-finding';

// 'overlay' is the legacy dark fullscreen panel; 'sidebar' renders the same
// details for the DocumentViewer's light, narrow sidebar.
type FindingsControllerVariant = 'overlay' | 'sidebar';

interface FindingsControllerProps {
  itemsOnPage: number;
  variant?: FindingsControllerVariant;
}

export const FindingsController: FC<FindingsControllerProps> = (props) => {
  const { itemsOnPage, variant = 'overlay' } = props;
  const { t } = useTranslation();
  const { document } = useViewerContext();
  const projectId = useProjectId();
  const packageId = usePackageId();
  const [getFinding] = useLazyGetFindingQuery();

  const [finding, setFinding] = useState<Finding>();
  const serviceName = useServiceNameOrNull();
  const comparisonExtractLabelKeys = findServiceConfigByRoute(serviceName)
    ?.comparisonExtractLabelKeys ?? { primary: 'findings.extract', reference: 'findings.extract' };

  const messageContext = useMessageContext(finding);

  useEffect(() => {
    if (document && document.type === DocumentType.FINDING) {
      getFinding({ projectId, packageId, findingId: document.findingId })
        .unwrap()
        .then((result) => setFinding(result))
        .catch((error) => console.error(error));
    }
  }, [document, getFinding, packageId, projectId]);

  if (document && document.type === DocumentType.FINDING) {
    const isSidebar = variant === 'sidebar';
    const overlayContentMaxHeight = document.refContent ? 'max-h-[13vh]' : 'max-h-[40vh]';
    return (
      document && (
        <div
          className={clsx(
            'flex flex-col',
            isSidebar
              ? 'text-day-neutral-dark'
              : 'w-full h-full text-lg justify-between text-white',
          )}
        >
          <div className={clsx('flex flex-col', isSidebar ? 'gap-4' : 'justify-between my-3 mx-4')}>
            <div className={isSidebar ? undefined : 'mt-4 ml-4'}>
              {finding && <UpdateFindingButtons finding={finding} itemsOnPage={itemsOnPage} />}
            </div>
            <div className={isSidebar ? undefined : 'py-10 pl-4 max-w-[50vw]'}>
              <div className={isSidebar ? 'text-lg font-semibold' : 'text-2xl'}>
                {t([
                  `dashboard.conflict-title.${document.conflictType}`,
                  'dashboard.conflict-title.__fallback',
                ])}
              </div>

              <div
                className={clsx(
                  'font-bold whitespace-pre-line',
                  isSidebar ? 'mt-2 text-sm' : 'my-3 text-base',
                )}
              >
                {getFindingMessage(document, messageContext, t)}
              </div>

              <div className={isSidebar ? 'mt-4 flex flex-col gap-4' : '-mx-4'}>
                {document.content && (
                  <div className={isSidebar ? 'text-sm' : 'text-base p-4'}>
                    <div className="font-bold">
                      {isDocumentDoubleViewerCapable(document)
                        ? t(comparisonExtractLabelKeys.primary)
                        : t('findings.extract')}
                      :
                    </div>
                    <div
                      className={clsx(
                        'italic overflow-y-auto',
                        isSidebar ? 'max-h-40' : overlayContentMaxHeight,
                      )}
                    >
                      {document.content}
                    </div>
                  </div>
                )}
                {isDocumentDoubleViewerCapable(document) && document.refContent && (
                  <div className={clsx('relative', isSidebar ? 'text-sm' : 'text-base p-4')}>
                    <div className="font-bold">{t(comparisonExtractLabelKeys.reference)}:</div>
                    <div
                      className={clsx(
                        'italic overflow-y-auto',
                        isSidebar ? 'max-h-40' : 'max-h-[13vh]',
                      )}
                    >
                      {document.refContent}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )
    );
  }
};
